import * as React from 'react';
import {useEffect, useState} from 'react';
import Box from '@mui/material/Box';
import {Backdrop, CircularProgress, Divider, Grid, Snackbar} from "@mui/material";
import AirQualityGauge from "../../components/AirQualityGauge/AirQualityGauge";
import LocationOnIcon from '@mui/icons-material/LocationOn';
import moment from "moment";
import 'moment/locale/de';
import styles from './styles.module.css';
import {CurrentAirQualityData} from "../../types/sensordata.types";
import BoschAPIService from "../../api/bosch-api.client";
import {
    getMajorPollutantBundesumweltamt,
    getMajorPollutantIndexLevelBundesumweltamt,
    getMajorPollutantValue,
} from "../../helper/airQualityHelper";
import AqiDetails from "../../components/AqiDetails/AqiDetails";
import WidgetFooter from "../../components/WidgetFooter/WidgetFooter";
import {AQIPollution, GaugeLevelMode} from "../../types/common.types";
import {BundesumweltamtAQI} from "../../types/bundesumweltam.types";

const AirQualityContainer = (): JSX.Element => {
    //const {t} = useTranslation();
    const [openWarning, setOpenWarning] = useState(false);
    const [currentAQIData, setCurrentAQIData] = useState<CurrentAirQualityData>();
    const [currentAQIGaugeData, setCurrentAQIGaugeData] = useState<AQIPollution>({
        majorIndexLevel: '',
        majorPollutant: '',
        measuringDate: ''
    });
    const [showLoadingSpinner, setShowLoadingSpinner] = React.useState(false);
    const [stationId]: any = React.useState(process.env.REACT_APP_STATION_ID);
    const gaugeLevelMode: GaugeLevelMode = process.env.REACT_APP_AQI_GAUGE_LEVEL_MODE === '0' ? GaugeLevelMode.FIVE : GaugeLevelMode.SIX;

    // Initialer Daten Load
    useEffect(() => {

        function loadAqiMainWidgetData() {
            let apiActiveCount = 0;

            if (process.env.REACT_APP_AQI_BOSCH_V1_API_ENABLED === 'true') {
                apiActiveCount = apiActiveCount + 1;
            }

            if (process.env.REACT_APP_AQI_BUNDESUMWELTAMT_API_ENABLED === 'true') {
                apiActiveCount = apiActiveCount + 1;
            }

            if (process.env.REACT_APP_AQI_BOSCH_CUMULATED_LUDWIGSBURG_API_ENABLED === 'true') {
                apiActiveCount = apiActiveCount + 1;
            }

            if (apiActiveCount > 1) {
                setOpenWarning(true);
            } else {
                setShowLoadingSpinner(true);
                if (process.env.REACT_APP_AQI_BOSCH_V1_API_ENABLED === 'true') {
                    getBoschV1AqiData();
                }

                if (process.env.REACT_APP_AQI_BUNDESUMWELTAMT_API_ENABLED === 'true') {
                    getBundesumweltamtAQIData();
                }

                if (process.env.REACT_APP_AQI_BOSCH_CUMULATED_LUDWIGSBURG_API_ENABLED === 'true') {
                    getBoschLudwigsburgAqiData();
                }
            }
        }

        function getBoschLudwigsburgAqiData() {
            if (stationId < 3) {
                BoschAPIService.getCumulativeLudwigsburgAQIData()
                    .then(function (response: any) {
                        setCurrentAQIData(response.data[0]);
                        setCurrentAQIGaugeData({
                            majorIndexLevel: getMajorPollutantValue(response.data[0]).index_level,
                            majorPollutant: response.data[0]?.payload.MajorPollutant ?? '',
                            measuringDate: response.data[0]?.payload.UTC ?? ''
                        })

                        setShowLoadingSpinner(false);
                    })
                    .catch((e: Error) => {
                        console.log(e);
                    });
            }
        };

        function getBundesumweltamtAQIData() {

            const requestOptions = {
                method: 'GET',
                redirect: 'follow',
                accepts: 'application/json'
            };

            const stationId: number = Number(String(process.env.REACT_APP_AQI_BUNDESUMWELTAMT_STATION_ID));
            if (stationId > 0) {
                const requestURI = 'https://zlfb4k4dmj.execute-api.eu-central-1.amazonaws.com/bua-v2/' + stationId;

                // @ts-ignore
                fetch(requestURI, requestOptions)
                    .then(response => response.text())
                    .then(result => {
                        const res = JSON.parse(result).data;

                        const resArray = res[stationId];
                        const size = Object.keys(resArray).length;
                        const mesDataKey: string = Object.keys(resArray)[size - 1];
                        const mesDataVAlue = resArray[mesDataKey];

                        const buaResult: BundesumweltamtAQI = {
                            dateMeasuringEnd: mesDataVAlue[0],
                            airQualityIndexLevel: mesDataVAlue[1],
                            dataIncomplete: mesDataVAlue[2],
                            pollutant1: mesDataVAlue[3],
                            pollutant2: mesDataVAlue[4],
                            pollutant3: mesDataVAlue[5],
                        }

                        let highestIndexLevel = 0;
                        let majorPollutant = '';

                        const p1 = [].concat(buaResult.pollutant1);
                        if (p1.length === 4 && p1[2] > highestIndexLevel) {
                            highestIndexLevel = p1[2];
                            majorPollutant = getMajorPollutantBundesumweltamt(p1[0]);
                        }

                        const p2 = [].concat(buaResult.pollutant2);
                        if (p2.length === 4 && p2[2] > highestIndexLevel) {
                            highestIndexLevel = p2[2];
                            majorPollutant = getMajorPollutantBundesumweltamt(p2[0]);
                        }

                        const p3 = [].concat(buaResult.pollutant3);
                        if (p3.length === 4 && p3[2] > highestIndexLevel) {
                            highestIndexLevel = p3[2];
                            majorPollutant = getMajorPollutantBundesumweltamt(p3[0]);
                        }

                        setCurrentAQIGaugeData({
                            majorIndexLevel: getMajorPollutantIndexLevelBundesumweltamt(highestIndexLevel),
                            majorPollutant: majorPollutant,
                            measuringDate: buaResult.dateMeasuringEnd
                        })

                        setShowLoadingSpinner(false);

                    })
                    .catch(error => console.log('error', error));
            }

        };

        function getBoschV1AqiData() {
            if (stationId > 0) {
                BoschAPIService.getCurrentAQIData(stationId)
                    .then(function (response: any) {
                        setCurrentAQIData(response.data[0]);

                        setCurrentAQIGaugeData({
                            majorIndexLevel: getMajorPollutantValue(response.data[0]).index_level,
                            majorPollutant: response.data[0]?.payload.MajorPollutant ?? '',
                            measuringDate: response.data[0]?.payload.UTC ?? ''
                        })

                        setShowLoadingSpinner(false);
                    })
                    .catch((e: Error) => {
                        console.log(e);
                    });
            }
        };

        // 1 initialer Aufrauf
        loadAqiMainWidgetData();

        // neue Daten alle 45 Minuten
        const interval = setInterval(() => {
            loadAqiMainWidgetData();
        }, 2700000); // 2700000
        return () => clearInterval(interval);
    }, [stationId]);


    moment().locale('DE');

    const handleCloseLoadingSpinner = () => {
        setShowLoadingSpinner(false);
    };

    const aqiDetails = (process.env.REACT_APP_AQI_DETAILS_VISIBLE && JSON.parse(process.env.REACT_APP_AQI_DETAILS_VISIBLE)) ?
        <div className={styles.bottomMargin}>
            <AqiDetails
                setShowLoadingSpinner={setShowLoadingSpinner}
                currentAQIData={currentAQIData}/>
        </div> : null;

    const handleCloseWarning = (event: React.SyntheticEvent | Event, reason?: string) => {
        if (reason === 'clickaway') {
            return;
        }
        setOpenWarning(false);
    };

    const accentBackgroundColor = {background: process.env.REACT_APP_BACKGROUND_COLOR ? process.env.REACT_APP_BACKGROUND_COLOR : '#fffff'};

    return (
        <React.Fragment>
            <Backdrop
                sx={{color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1}}
                open={showLoadingSpinner}
                onClick={handleCloseLoadingSpinner}>
                <CircularProgress color="inherit"/>
            </Backdrop>

            <Box sx={{flexGrow: 1}} className={styles.bottomMargin}>
                <Grid container spacing={3}>

                    <Grid item xs={12}>
                        <h2>LUFTQUALITÄT</h2>
                        <Divider/>
                    </Grid>

                    <Grid item xs={3} sm={6} className={styles.textAlignRight}>
                        <LocationOnIcon sx={{fontSize: 40}}/>
                    </Grid>

                    <Grid item xs={9} sm={6} className={styles.textAlignLeft}>
                        <span className={styles.meterLocation}>{process.env.REACT_APP_TOWN_LABEL}</span> <br/>
                    </Grid>

                    <Grid item xs={12}>
                    <span className={styles.gaugeHeader} style={accentBackgroundColor}>
                        AIR QUALITY INDEX
                    </span>
                    </Grid>
                    <Grid item xs={12}>
                        <Box display="flex" justifyContent="center">
                            <AirQualityGauge
                                aqiPollution={currentAQIGaugeData}
                                gaugeLevel={gaugeLevelMode}
                            />
                        </Box>
                        <br/>
                        <Divider/>
                    </Grid>
                </Grid>
            </Box>

            {aqiDetails}

            <WidgetFooter/>

            <Snackbar
                open={openWarning}
                autoHideDuration={6000}
                onClose={handleCloseWarning}
                message="Falsche Widget Konfiguration (mehr als eine API aktiviert)"
            />
        </React.Fragment>
    );
}

export default AirQualityContainer;